import { Scene } from 'phaser';
import { criarBotaoAnotacoes, instalarAnotacoes } from '../components/AnotacoesVersus';
import { BotaoArcade } from '../components/BotaoArcade';
import { desenharPaginaLima, desenharPainel, desenharPainelDuplo } from '../components/cenario';
import { ChatVersus } from '../components/ChatVersus';
import { ModalArcade } from '../components/ModalArcade';
import { abrirVisualizadorDeMaterial } from '../components/visualizadorDeMaterial';
import { MolduraPixel } from '../components/MolduraPixel';
import { criarTextoRico } from '../components/TextoRico';
import { GAME, SCENES, TEX } from '../config/constants';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { Sessao } from '../services/Sessao';
import { alternarAnexo, criarChatVersus, criarSalaVersus, decidirResultadoDoEnvio, decidirToqueDeEnvio, deveLimparAnexo, ProgressoVersus, quebrarEmDuas, rodadaDoDestino, textoDoAviso, limitarAnexos } from '../services/versus';
import { t } from '../services/textos';

// Arte de cada case, pela ordem da rodada (Figma, quadros 74–77); o texto vem do catálogo.
const ARTE = [
    { retrato: TEX.VS_CLIENTE_MARCOS, foto: TEX.VS_FOTO_MARCOS },
    { retrato: TEX.VS_CLIENTE_FERNANDA, foto: TEX.VS_FOTO_FERNANDA }
];
const MARROM = 0xb8730a;
const AMARELO = 0xfbee8f;
const CORRIDO = ARCADE_TEXT.introducao.fontFamily;
const PIXEL = ARCADE_TEXT.botao.fontFamily;

/**
 * Modo Versus — instrução, case e chat com a IA (Figma, quadros 74–78 e
 * 24–26). Cada participante conversa com a IA na própria conversa; o
 * argumento final fica na cena seguinte.
 *
 * A fase é síncrona: sem rede não há resposta, e nada é enfileirado para
 * depois — o participante vê o aviso e reenvia.
 */
export class VersusScene extends Scene {
    constructor () {
        super(SCENES.VERSUS);
    }

    async create () {
        // `scene.start` repetido reinicia esta mesma instância e o `create` antigo, parado num `await`,
        // retomaria depois do reinício (a cena volta a estar ativa) e desenharia uma 2ª cópia por cima.
        const geracao = this.geracao = (this.geracao ?? 0) + 1;
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.fonte = this.registry.get('fonte');
        this.progresso = new ProgressoVersus(Sessao.atual.participanteId);
        this.modal = null;
        this.enviando = false;
        this.dons = [];
        // A instância é reaproveitada a cada `scene.start`/`restart` (troca de case): o pop-up de anotações
        // que estava aberto morre com a cena, mas `this.popup` ficaria preso e `abrirAnotacoes` retornaria
        // na hora, deixando o botão "Anotações" morto no case seguinte.
        this.popup = null;
        this.grade = null;
        this.editor = null;
        this.notas = null;

        desenharPaginaLima(this, { titulo: t('versus.titulo'), rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0 });
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => !this.modal && this.scene.start(SCENES.AGUARDE, {}));

        try {
            this.versus = await this.fonte.obterCatalogoVersus();
        } catch (falha) {
            console.error('[Versus] não foi possível carregar os cases.', falha);
            if (this.scene.isActive()) {
                this.add.text(640, 400, t('versus.erro_carregar'), ARCADE_TEXT.aviso).setOrigin(0.5);
                new BotaoArcade(this, 640, 500, { rotulo: t('geral.voltar'), aoClicar: () => this.scene.start(SCENES.AGUARDE, {}) });
            }
            return;
        }
        if (!this.scene.isActive() || geracao !== this.geracao) return;

        if (this.progresso.estado.concluido) {
            if (!Sessao.ehTeste) {
                this.scene.start(SCENES.VERSUS_RESULTADO);
                return;
            }
            this.progresso.reiniciar();
        }

        // AC-INT-018/020: o servidor diz quais rodadas já acabaram; sem leitura, vale o progresso local.
        const { turmaId, grupoId, participanteId } = Sessao.atual;
        this.sala = criarSalaVersus({
            fonte: this.fonte, rede: this.game.network, turmaId, grupoId, rodadas: this.versus.rodadas,
            aoMudar: () => this.aoMudarSala()
        });
        this.events.once('shutdown', () => this.sala.sair());
        await this.sala.entrar();
        if (!this.scene.isActive() || geracao !== this.geracao) return;

        // RI-MV-13: encerrada a rodada, abre a seguinte; depois da segunda, a espera.
        this.rodada = this.sala.lida
            ? rodadaDoDestino(this.sala.destino, this.versus.rodadas)
            : this.progresso.rodadaAtual(this.versus.rodadas);
        if (!this.rodada) {
            // `V12`/`DP-MV-065`: o case seguinte só abre com o facilitador liberando.
            if (this.sala.destino.etapa === 'aguardando_liberacao') {
                this.mostrarEsperaDoSegundoCase();
                return;
            }
            this.scene.start(SCENES.VERSUS_RESULTADO);
            return;
        }
        this.arte = ARTE[this.rodada.ordem - 1] ?? ARTE[0];
        // Case novo, anotações em branco (as do case anterior saem da vista).
        try { await this.fonte.limparAnotacoesVersus?.(this.rodada.ordem); } catch (falha) { console.error('[Versus] anotações não limpas.', falha); }

        // AC-INT-015/017: identidade da Sessao e `ordem` (não o id do catálogo); o histórico é o do servidor.
        this.chatLogica = criarChatVersus({
            fonte: this.fonte, progresso: this.progresso, turmaId, grupoId, participanteId, rodada: this.rodada
        });
        const leitura = await this.chatLogica.carregar();
        if (!this.scene.isActive() || geracao !== this.geracao) return;
        this.falhouLeitura = leitura.estado === 'falhou';

        this.desenharCase();
        this.desenharChat();
        if (this.falhouLeitura) this.chat.avisar('Conversa não carregada, envio bloqueado. Toque em enviar para tentar de novo.');

        const estado = this.progresso.rodada(this.rodada.id);
        const abertura = this.versus.rodadas[0]?.abertura ?? [];
        if (!this.progresso.estado.aberturaVista && abertura.length > 0) this.abrirAbertura(abertura);
        else if (!estado.caseVisto) this.abrirCase();
    }

    /** `AC-INT-020`: o líder (ou o facilitador) encerrou a rodada enquanto este tablet estava no chat. */
    aoMudarSala () {
        if (!this.scene.isActive()) return;
        // `V12`: na espera do case 2, a liberação (evento ou reconexão) abre o case.
        if (this.esperandoLiberacao) {
            if (this.sala.destino.etapa !== 'aguardando_liberacao') this.scene.restart();
            return;
        }
        if (!this.rodada) return;
        const { destino } = this.sala;
        if (destino.etapa === 'espera_cedula') this.scene.start(SCENES.VERSUS_RESULTADO);
        else if (destino.ordem !== this.rodada.ordem) this.scene.restart();
    }

    /** `V12`: o case 1 acabou e o 2 espera o facilitador liberar; a sala avisa quando abrir. */
    mostrarEsperaDoSegundoCase () {
        this.esperandoLiberacao = true;
        this.events.once('shutdown', () => { this.esperandoLiberacao = false; });
        this.add.text(640, 400, t('versus.aguarde_segundo_case'), { ...ARCADE_TEXT.aviso, align: 'center', wordWrap: { width: 900 } })
            .setOrigin(0.5);
    }

    /** A camada DOM fica sempre acima do canvas: some enquanto houver modal aberto. */
    mostrarDom (visivel) {
        this.dons.forEach((dom) => dom.setVisible(visivel));
        // O Phaser reaplica `display` a cada render e não conhece nós que ficaram fora de `dons`
        // (a conversa aparecia por cima do modal da rodada 2): `visibility` é independente dele.
        document.querySelectorAll('.chat-versus, .texto-versus').forEach((no) => {
            no.style.visibility = visivel ? '' : 'hidden';
        });
    }

    // -------------------------------------------------------------- coluna do case

    desenharCase () {
        const { caso, ordem } = this.rodada;
        const g = this.add.graphics();
        g.fillStyle(ARCADE_HEX.BRANCO, 0.4).fillRoundedRect(60, 180, 312, 589, 14);
        g.lineStyle(1, ARCADE_HEX.BRANCO, 1).strokeRoundedRect(60, 180, 312, 589, 14);
        g.lineStyle(1.4, ARCADE_HEX.TINTA, 1).lineBetween(86, 256, 346, 256);

        this.add.text(86, 232, t('versus.case', { ordem }), ARCADE_TEXT.tituloFase).setOrigin(0, 0.5);
        this.add.text(86, 284, caso.perfilRotulo, ARCADE_TEXT.manchete).setOrigin(0, 0.5);
        this.add.image(216, 534, this.arte.retrato).setOrigin(0.5, 1);

        MolduraPixel.desenhar(g, 81, 533, 270, 208, { fundo: AMARELO, fio: MARROM, fioMeio: AMARELO }, { esq: true, dir: true, tamanho: 8 });
        this.add.text(216, 553, t('versus.cliente', { nome: caso.nome, idade: caso.idade }).toUpperCase(), {
            fontFamily: PIXEL, fontSize: '15px', fontStyle: '700', color: ARCADE.TINTA
        }).setOrigin(0.5);

        const estilo = { fontFamily: CORRIDO, fontSize: '17px', fontStyle: '500', color: ARCADE.TINTA };
        let y = 584;
        [[t('versus.rotulo_perfil'), caso.perfilResumo], [t('versus.rotulo_objecao'), caso.objecaoResumo]].forEach(([rotulo, texto]) => {
            if (!texto) return;
            y += criarTextoRico(this, 100, y, 232, `**${rotulo}:** ${texto}`, estilo, { entrelinha: 22 }).altura + 10;
        });

        // RI-MV-05: "Ver mais" abre a ficha completa do case.
        this.add.text(216, 716, t('versus.ver_mais'), { fontFamily: PIXEL, fontSize: '14px', fontStyle: '700', color: '#9a5f08' }).setOrigin(0.5);
        this.add.zone(216, 716, 200, 48).setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.abrirCase());
    }

    // ---------------------------------------------------------------------- chat

    desenharChat () {
        const g = this.add.graphics();
        g.fillStyle(ARCADE_HEX.BRANCO, 0.4).fillRoundedRect(414, 180, 806, 499, 14);
        g.lineStyle(1, ARCADE_HEX.BRANCO, 1).strokeRoundedRect(414, 180, 806, 499, 14);
        MolduraPixel.desenhar(g, 438, 539, 758, 120,
            { fundo: ARCADE_HEX.CAMPO, fio: ARCADE_HEX.FIO_CAMPO, fioMeio: ARCADE_HEX.BRANCO }, { esq: true, dir: true, tamanho: 10 });

        // RN-MV-07: o histórico da rodada fica salvo e volta inteiro. RI-MV-02: a IA abre a conversa.
        this.chat = new ChatVersus(this, { x: 439, y: 198, largura: 756, altura: 326 });
        this.aplicarHistorico();

        this.area = document.createElement('textarea');
        this.area.className = 'texto-versus';
        this.area.placeholder = t('versus.placeholder_mensagem');
        this.area.maxLength = this.versus.tetos.caracteresMensagem;
        this.area.setAttribute('aria-label', 'Mensagem para a IA');
        this.area.style.width = '714px';
        this.area.style.height = '48px';
        // O Enter só quebra a linha: o envio é exclusivo do botão ENVIAR.
        this.area.addEventListener('keydown', (evento) => evento.stopPropagation());   // o Phaser escuta o teclado na janela
        this.dons = [this.chat.dom, this.add.dom(817, 572, this.area)];

        // RI-MV-03: anexar material abre o seletor.
        // O botão fica alinhado à esquerda, junto da borda do campo de mensagem.
        // O botão é só o texto sublinhado (o sublinhado acompanha o rótulo, que muda entre "anexar" e
        // "trocar"); fica abaixo do campo de texto com folga, para o que se digita não encostar nele.
        this.sublinhadoAnexo = this.add.graphics();
        this.rotuloAnexo = this.add.text(462, 626, '', { fontFamily: PIXEL, fontSize: '14px', fontStyle: '400', color: ARCADE.BRANCO })
            .setOrigin(0, 0.5);
        this.atualizarAnexos();
        // Área de toque (safe zone) bem maior que o texto.
        this.add.zone(440, 626, 300, 64).setOrigin(0, 0.5).setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.abrirMateriais());

        // RI-MV-04: o botão ENVIAR (e só ele; o Enter do teclado não envia) manda a mensagem para a IA.
        new BotaoArcade(this, 1088, 626, { rotulo: t('versus.enviar'), largura: 200, altura: 48, aoClicar: () => this.enviar() });

        // RI-MV-09 e RI-MV-06: anotações (à esquerda) e argumento (à direita) dividem a linha, com o mesmo
        // tamanho; as anotações abrem na própria tela do chat, sem passar pelo argumento.
        criarBotaoAnotacoes(this, 610, 734, { largura: 392, altura: 70, preto: true });
        new BotaoArcade(this, 1024, 734, {
            rotulo: t('versus.escrever_argumento'), largura: 392,
            aoClicar: () => !this.modal && this.scene.start(SCENES.VERSUS_ARGUMENTO)
        });
    }

    /** Boas-vindas + conversa do servidor (`DP-INT-034`: vazia quando a leitura falhou, nunca a cópia local). */
    aplicarHistorico () {
        this.chat.adicionar({ autor: 'ia', texto: t('versus.boas_vindas'), copiavel: false });
        this.chatLogica.mensagens.forEach((mensagem) => this.chat.adicionar({
            autor: mensagem.autor, texto: mensagem.texto, anexos: this.titulos(mensagem.materiais ?? [])
        }));
    }

    /** `AC-INT-032`: refaz a conversa na tela a partir do que `chatLogica` adotou do servidor. */
    redesenharHistorico () {
        this.chat.limpar();
        this.aplicarHistorico();
    }

    titulos (ids) {
        return ids.map((id) => this.versus.materiais.find((material) => material.id === id)?.titulo ?? id);
    }

    atualizarAnexos () {
        // RN-MV-08: até dois materiais; com algum anexado, o botão mostra quantos e passa a trocar.
        const quantidade = limitarAnexos(this.progresso.rodada(this.rodada.id).materiais).length;
        this.rotuloAnexo.setText(quantidade > 0 ? t('versus.trocar_material', { quantidade }) : t('versus.anexar_material'));
        // Sublinhado: Phaser não tem `text-decoration`, então o traço é desenhado sob o rótulo.
        const { x, y, width, height } = this.rotuloAnexo;
        const base = y + height / 2 + 3;
        this.sublinhadoAnexo.clear().lineStyle(2, ARCADE_HEX.BRANCO, 1)
            .lineBetween(x, base, x + width, base);
    }

    async enviar () {
        if (this.modal || this.enviando || this.sala.chatTravado(this.rodada.ordem)) return;
        const texto = this.area.value.trim();
        // `AC-INT-033`: a decisão é pura (`decidirToqueDeEnvio`); a cena só executa.
        const { acao } = decidirToqueDeEnvio({
            texto, podeEnviar: this.chatLogica.podeEnviar, maximo: this.versus.tetos.caracteresMensagem
        });
        if (acao === 'ignorar') return;
        if (acao === 'reler') return this.reler();
        this.enviando = true;

        const materiais = limitarAnexos(this.progresso.rodada(this.rodada.id).materiais);     // RN-MV-08
        this.chat.limparAviso();
        const linha = this.chat.adicionar({ autor: 'eu', texto, anexos: this.titulos(materiais) });
        this.area.value = '';
        this.chat.mostrarEspera();

        try {
            const resultado = await this.chatLogica.enviar({ texto, materiais });
            if (!this.scene.isActive()) return;
            this.chat.ocultarEspera();
            // `AC-INT-032`: a decisão é pura (`decidirResultadoDoEnvio`); a cena só executa.
            const acoes = decidirResultadoDoEnvio(resultado);
            if (!acoes.manterLinhaEnviada) linha.remove();
            if (acoes.textoDaIa) this.chat.adicionar({ autor: 'ia', texto: acoes.textoDaIa });
            if (acoes.devolverAoCampo && this.area.value === '') this.area.value = texto;
            // O histórico lido do servidor é redesenhado: a conversa na tela é a do servidor.
            if (acoes.redesenharHistorico) this.redesenharHistorico();
            if (acoes.aviso) this.chat.avisar(textoDoAviso(acoes.aviso));
            // RN-MV-08: com a mensagem gravada, o material anexado sai do campo. Se o participante
            // trocou o anexo enquanto esperava a IA, o novo fica.
            const atuais = this.progresso.rodada(this.rodada.id).materiais;
            if (deveLimparAnexo(resultado) && materiais.length > 0 && atuais.join('|') === materiais.join('|')) {
                this.progresso.anexar(this.rodada.id, []);
                this.atualizarAnexos();
            }
        } finally {
            this.enviando = false;
        }
    }

    /** `AC-INT-033`: campo vazio com o envio bloqueado -> relê a conversa (sem linha otimista nem POST). */
    async reler () {
        this.enviando = true;
        this.chat.limparAviso();
        this.chat.mostrarEspera();

        try {
            const resultado = await this.chatLogica.enviar({ texto: '', materiais: [] });
            if (!this.scene.isActive()) return;
            this.chat.ocultarEspera();
            const acoes = decidirResultadoDoEnvio(resultado);
            // De propósito, só `redesenharHistorico` e o aviso rodam aqui: `devolverAoCampo`, `manterLinhaEnviada` e
            // `textoDaIa` são ignorados porque `reler()` só roda com campo vazio e envio bloqueado (texto '' — nada a
            // devolver, sem linha otimista nem resposta da IA). Se passar a ser chamado com texto, execute-as também
            // (DP-INT-054, RN-INT-022/023).
            if (acoes.redesenharHistorico) this.redesenharHistorico();
            if (acoes.aviso) this.chat.avisar(textoDoAviso(acoes.aviso));
        } finally {
            this.enviando = false;
        }
    }

    // -------------------------------------------------------------------- modais

    /** RI-MV-01: Iniciar abre o primeiro case. */
    abrirAbertura ([manchete, ...demais]) {
        this.mostrarDom(false);
        this.modal = new ModalArcade(this, {
            titulo: t('versus.titulo'), largura: 860, centroY: 392, botaoY: 632,
            blocos: [{ texto: manchete, peso: '700', maiusculas: true, largura: 700 }, ...demais.map((texto) => ({ texto }))],
            botao: {
                rotulo: t('geral.iniciar'),
                aoClicar: () => {
                    this.progresso.marcarAberturaVista();
                    this.abrirCase();
                }
            },
            aoFechar: () => {
                this.modal = null;
                this.mostrarDom(true);
            }
        });
    }

    /** Ficha completa do case (quadros 76 e 77). "Ir ao chat" leva à interface da IA. */
    abrirCase () {
        if (this.modal) return;
        this.mostrarDom(false);
        const { caso } = this.rodada;
        const camada = this.add.container(0, 0).setDepth(300);
        const fundo = this.add.rectangle(GAME.CENTER_X, GAME.CENTER_Y, GAME.LARGURA_TOTAL, GAME.HEIGHT, 0x000000, 0.72).setInteractive();
        const g = this.add.graphics();
        desenharPainelDuplo(g, 130, 47, 1020, 723, { fora: MARROM, vao: AMARELO, dentro: MARROM, fundo: AMARELO });
        camada.add([fundo, g, this.add.image(188, 95, this.arte.foto).setOrigin(0),
            this.add.text(416, 116, t('versus.cliente', { nome: caso.nome, idade: caso.idade }).toUpperCase(), ARCADE_TEXT.tituloFase).setOrigin(0, 0.5)]);

        const secoes = [
            { rotulo: t('versus.case_contexto'), texto: caso.contexto, x: 414, largura: 690 },
            { rotulo: t('versus.case_objecao'), texto: caso.objecao, x: 180, largura: 920 },
            { rotulo: t('versus.case_missao'), texto: caso.missao, x: 180, largura: 920 }
        ];
        // O texto encolhe até a ficha caber acima do botão.
        let pecas = [];
        for (const tamanho of [22, 20, 18, 16]) {
            pecas.forEach((peca) => peca.destroy());
            pecas = [];
            let y = 150;
            secoes.forEach(({ rotulo, texto, x, largura }, i) => {
                if (i > 0) {
                    y = Math.max(y, i === 1 ? 313 : 0);
                    const fio = this.add.graphics().lineStyle(1, MARROM, 0.8);
                    for (let px = 180; px < 1100; px += 7) fio.lineBetween(px, y, px + 4, y);
                    pecas.push(fio);
                    y += 18;
                }
                const titulo = this.add.text(x, y, rotulo, { fontFamily: CORRIDO, fontSize: '22px', fontStyle: '700', color: ARCADE.TINTA });
                const corpo = this.add.text(x, y + 33, texto, {
                    fontFamily: CORRIDO, fontSize: `${tamanho}px`, fontStyle: '500', color: ARCADE.TINTA,
                    lineSpacing: 5, wordWrap: { width: largura }
                });
                pecas.push(titulo, corpo);
                y += 33 + corpo.height + 16;
            });
            if (y <= 648) break;
        }
        camada.add(pecas);

        const fechar = () => {
            camada.destroy();
            this.modal = null;
            this.mostrarDom(true);
            this.progresso.marcarCaseVisto(this.rodada.id);
        };
        camada.add(new BotaoArcade(this, 640, 692, { rotulo: t('versus.ir_ao_chat'), aoClicar: fechar }));
        this.modal = { fechar };
    }

    /** Seletor de materiais (quadros "Box de Materiais"): lista fechada vinda do catálogo. */
    abrirMateriais () {
        if (this.modal || this.enviando) return;
        this.mostrarDom(false);
        const estado = this.progresso.rodada(this.rodada.id);
        // RN-MV-08: até dois materiais; com dois marcados, escolher outro tira o mais antigo.
        let escolhidos = limitarAnexos(estado.materiais);
        const pinturas = [];

        const camada = this.add.container(0, 0).setDepth(300);
        const fundo = this.add.rectangle(GAME.CENTER_X, GAME.CENTER_Y, GAME.LARGURA_TOTAL, GAME.HEIGHT, 0x000000, 0.6).setInteractive();
        const g = this.add.graphics();
        desenharPainel(g, 172, 74, 936, 653);
        const fechar = () => {
            if (visualizador) return;
            camada.destroy();
            this.modal = null;
            this.mostrarDom(true);
        };
        camada.add([fundo, g,
            this.add.text(640, 144, t('versus.materiais_titulo'), ARCADE_TEXT.tituloPopup).setOrigin(0.5),
            this.add.text(640, 186, t('versus.materiais_instrucao'), { ...ARCADE_TEXT.aviso, fontSize: '20px' }).setOrigin(0.5),
            this.add.image(1021, 143, TEX.VS_BTN_FECHAR),
            this.add.zone(1021, 143, 48, 48).setInteractive({ useHandCursor: true }).on('pointerup', fechar)]);

        const botao = new BotaoArcade(this, 804, 654, {
            rotulo: t('versus.anexar'), largura: 300, habilitado: false,
            aoClicar: () => {
                if (visualizador) return;
                this.progresso.anexar(this.rodada.id, escolhidos);
                this.atualizarAnexos();
                fechar();
            }
        });
        // RI-MV-03b: o material marcado pode ser lido antes de anexar, no mesmo visualizador da biblioteca.
        // Com dois marcados, Visualizar abre o último tocado.
        let visualizador = null;
        const marcado = () => this.versus.materiais.find((item) => item.id === escolhidos.at(-1))?.material ?? null;
        const ver = new BotaoArcade(this, 476, 654, {
            rotulo: t('versus.visualizar'), largura: 300, habilitado: false,
            cores: { fundo: ARCADE_HEX.TINTA, fio: ARCADE_HEX.TINTA, fioMeio: ARCADE_HEX.BRANCO },
            aoClicar: () => {
                const material = marcado();
                if (!material || visualizador) return;
                visualizador = abrirVisualizadorDeMaterial(this, material, {
                    profundidade: 400, aoFechar: () => { visualizador = null; }
                });
            }
        });
        // Anexar habilita com algo marcado — ou para confirmar que tudo foi desmarcado.
        const atualizarBotao = () => {
            botao.setHabilitado(escolhidos.length > 0 || estado.materiais.length > 0);
            ver.setHabilitado(Boolean(marcado()));
        };

        const { materiais } = this.versus;
        const linhas = materiais.length <= 4
            ? [materiais]
            : [materiais.slice(0, Math.ceil(materiais.length / 2)), materiais.slice(Math.ceil(materiais.length / 2))];
        linhas.forEach((linha, l) => {
            const passo = Math.min(205, 820 / linha.length);
            linha.forEach((material, i) => {
                const cx = 640 + (i - (linha.length - 1) / 2) * passo;
                const topo = 214 + l * 190;
                const pasta = this.add.image(cx, topo, TEX.PASTA_ABERTA).setOrigin(0.5, 0);
                const rotulo = this.add.text(cx, topo + 111, quebrarEmDuas(material.titulo.toUpperCase()), ARCADE_TEXT.rotuloMaterial)
                    .setOrigin(0.5, 0);
                if (rotulo.width > passo - 6) rotulo.setScale((passo - 6) / rotulo.width);
                const pintar = () => {
                    const marcado = escolhidos.includes(material.id);
                    if (marcado) pasta.setTint(0x8fd67a).setAlpha(0.7);
                    else pasta.clearTint().setAlpha(1);
                    rotulo.setColor(marcado ? ARCADE.VERDE : ARCADE.TINTA);
                };
                const zona = this.add.zone(cx, topo + 78, passo - 8, 160).setInteractive({ useHandCursor: true })
                    .on('pointerup', () => {
                        escolhidos = alternarAnexo(escolhidos, material.id);
                        // Repinta todos: o que estava marcado deixa de estar.
                        pinturas.forEach((repintar) => repintar());
                        atualizarBotao();
                    });
                pinturas.push(pintar);
                pintar();
                camada.add([pasta, rotulo, zona]);
            });
        });
        if (materiais.length === 0) {
            camada.add(this.add.text(640, 380, t('versus.sem_materiais'), { ...ARCADE_TEXT.aviso, align: 'center', wordWrap: { width: 760 } }).setOrigin(0.5));
        }
        camada.add([ver, botao]);
        atualizarBotao();
        this.modal = { fechar };
    }
}

instalarAnotacoes(VersusScene);
